import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { maybePromptForPush } from '../services/softPushPrompt';
import { collection, addDoc, serverTimestamp, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';
import { PEDAL_COLORS, trackMissingPedal } from '../utils/pedalDatabase';
import { useReverbCombinedSuggestions } from '../hooks/useReverbProductSuggestions';
import ConditionDescriptionPopover from './ConditionDescriptionPopover';
import { CONDITION_DESCRIPTIONS } from '../utils/conditionDescriptions';
import { trackWantAdded } from '../utils/effectAnalytics';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { validateMessage } from '../utils/profanityFilter';
import { getWantMismatchFields } from '../utils/matchingLogic';
import { findBlockingDuplicate, wantIdentityKey } from '../utils/wantIdentity';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import KeywordExclusionInput, { MAX_EXCLUSION_KEYWORDS } from './KeywordExclusionInput';
import { EFFECT_TYPES } from '../utils/effectTypes';
import { FORM_SELECT_STYLE } from '../utils/formSelectStyle';
import OnboardingTip from './OnboardingTip';
import useOnboardingTips from '../hooks/useOnboardingTips';

// How long to wait after the last keystroke before checking for a duplicate.
// Only the advisory banner is delayed; the submit-time check is immediate.
const DUPLICATE_CHECK_DEBOUNCE_MS = 350;

const AddWantForm = ({ editingWant, matchListing, onFinishEdit, onWantAdded, onCancelPrefill, wideSpacing = false, hideNotes = false, suppressSuccessAlert = false }) => {
  const { firestoreUser } = useContext(UserContext);
  // First-visit tip (Jason, 2026-10-20): the "Never show me" card, when shown.
  const { activeTip: wantTip, dismiss: dismissWantTip, delay: wantTipDelay } = useOnboardingTips([{ id: 'addWantExclusions', ready: !hideNotes }]);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsub();
  }, []);
  
  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str) => {
    // Roman numerals that should be fully capitalized
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
    
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Check if the word is a Roman numeral
        const upperWord = word.toUpperCase();
        if (romanNumerals.includes(upperWord)) {
          return upperWord;
        }
        // Otherwise, capitalize first letter only
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };
  
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [condition, setCondition] = useState('');
  const [decade, setDecade] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [effectTypes, setEffectTypes] = useState([]);
  const [excludedKeywords, setExcludedKeywords] = useState([]);

  // ── Combined pedal search (mirrors AddPedalForm Step 1) ────────────────────
  // A single "Search for your pedal" box backed by the SAME hook + matcher +
  // dropdown UI as the Add Pedal wizard. Selecting a canonical row locks in the
  // exact make/model. Wants stay flexible: the dropdown also offers a brand-only
  // pick ("Any <Brand> pedal"), and the manual fallback lets the model be left
  // blank — so brand-only (and effect-type-only) wants are still possible.
  const [pedalQuery, setPedalQuery] = useState('');
  const [showCombinedSuggestions, setShowCombinedSuggestions] = useState(false);
  const [pedalSelected, setPedalSelected] = useState(false);
  const [brandOnly, setBrandOnly] = useState(false);
  const [showEffectTypes, setShowEffectTypes] = useState(false);
  const conditionSelectRef = useRef(null);
  const [conditionPopoverDismissed, setConditionPopoverDismissed] = useState(false);

  // Reverb-powered combined suggestions (debounced/cached in the hook — the same
  // engine, matcher and ranking the Add Pedal wizard uses).
  const { suggestions: combinedSuggestions, loading: combinedLoading } = useReverbCombinedSuggestions({
    query: pedalQuery,
  });

  // Only offer "Any <Brand> pedal" when the current results are unambiguously a
  // single brand (typing "boss" → all Boss; "electro" → all EHX). A cross-brand
  // query like "delay" yields NO brand-only row, so we never guess wrong.
  const brandOnlyCandidate =
    combinedSuggestions.length > 0 &&
    combinedSuggestions.every((s) => s.make && s.make === combinedSuggestions[0].make)
      ? combinedSuggestions[0].make
      : '';

  // Settled no-match gate (same 900ms idle as Add Pedal): Reverb's word-based CSP
  // search returns empty mid-word, so we only surface the manual fallback once the
  // query has stayed empty and idle for a beat — never flashing while still typing.
  const combinedQueryTrimmed = pedalQuery.trim();
  const combinedNoMatchRaw =
    !pedalSelected && combinedQueryTrimmed.length >= 2 && !combinedLoading && combinedSuggestions.length === 0;
  const [noMatchSettled, setNoMatchSettled] = useState(false);
  useEffect(() => {
    if (!combinedNoMatchRaw) { setNoMatchSettled(false); return undefined; }
    const t = setTimeout(() => setNoMatchSettled(true), 900);
    return () => clearTimeout(t);
  }, [combinedNoMatchRaw, combinedQueryTrimmed]);
  const combinedNoMatch = noMatchSettled;

  // Manual-entry safety net: while the user fills the manual Brand/Model fallback,
  // quietly re-search the catalog with their combined text and offer a tappable
  // "found it" card so they land on the standardized name (same as Add Pedal).
  const manualLookupQuery =
    combinedNoMatch && !pedalSelected ? `${make || ''} ${model || ''}`.trim() : '';
  const { suggestions: manualMatches } = useReverbCombinedSuggestions({ query: manualLookupQuery });
  
  // Duplicate detection state. Holds the colliding want rather than a flag, so
  // the warning can link straight to it.
  const [duplicateWant, setDuplicateWant] = useState(null);
  const isDuplicate = !!duplicateWant;

  // Whether that collision is solid enough to grey out the save button.
  // A brand with no model yet is ambiguous: it is a genuine collision if the
  // user is adding a second brand-only want, and a false alarm if they are
  // halfway through typing a specific pedal by hand and already have a
  // brand-only want for that brand. Warn either way, but only take the button
  // away once the pedal is actually named — a save button that greys itself out
  // mid-form reads as the form being broken. handleSubmit re-checks regardless,
  // so nothing gets through on the strength of this being permissive.
  //
  // During an edit, a stale banner from the add flow must not disable save when
  // the pedal identity is unchanged — e.g. "Not this? Search again" then picking
  // the same Spaceman-any again. findBlockingDuplicate would allow that save;
  // the button has to agree.
  const editingIdentityUnchanged = Boolean(
    editingWant &&
    wantIdentityKey({ make, model }) &&
    wantIdentityKey({ make, model }) === wantIdentityKey(editingWant)
  );
  const duplicateBlocksSave =
    isDuplicate && (Boolean(model) || brandOnly) && !editingIdentityUnchanged;

  // When editing a want that was reached from a listing whose criteria it does
  // NOT currently match, we auto-fill the offending fields to the listing's
  // values and flag them here ({ maxPrice: { from, to }, ... }) so the UI can
  // highlight exactly what changed. Cleared per-field once the user edits it.
  const [highlightFields, setHighlightFields] = useState({});

  const clearHighlight = (field) => {
    setHighlightFields((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Reusable gold-highlight style for auto-updated fields.
  const highlightInputStyle = { borderColor: '#C9A227', borderWidth: '1.5px', borderStyle: 'solid' };
  const HighlightCaption = ({ field }) => (
    highlightFields[field] ? (
      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#946F00', fontFamily: 'Nunito, sans-serif', textAlign: 'left' }}>
        Updated to match this listing{highlightFields[field].from ? ` (was ${highlightFields[field].from})` : ''}
      </div>
    ) : null
  );
  
  // Loading state to prevent double submissions
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to extract URL parameters for pre-filling
  const getUrlParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    
    // Parse effectTypes if present
    let effectTypesArray = [];
    const effectTypesParam = searchParams.get('effectTypes');
    if (effectTypesParam) {
      try {
        effectTypesArray = JSON.parse(effectTypesParam);
      } catch (e) {
        console.error('Error parsing effectTypes:', e);
        effectTypesArray = [];
      }
    }
    
    const params = {
      prefill: searchParams.get('prefill') === 'true',
      make: searchParams.get('make') || '',
      model: searchParams.get('model') || '',
      condition: searchParams.get('condition') || '',
      price: searchParams.get('price') || '',
      effectTypes: effectTypesArray
    };
    
    return params;
  }, [location.search]);

  // Load existing data into form if editing, or handle URL parameters for pre-filling
  useEffect(() => {
    if (editingWant) {
      setMake(editingWant.make || '');
      setModel(editingWant.model || '');
      setMaxPrice(editingWant.maxPrice || '');
      setCondition(editingWant.condition || '');
      setDecade(editingWant.decade || '');
      setDescription(editingWant.description || '');
      setColor(editingWant.color || '');
      setEffectTypes(editingWant.effectTypes || []);
      setExcludedKeywords(editingWant.excludedKeywords || []);

      // Reflect the saved pedal in the combined search box: show the selected
      // chip (brand-only if there's a make but no model).
      const editQuery = [editingWant.make, editingWant.model].filter(Boolean).join(' ').trim();
      setPedalQuery(editQuery);
      setPedalSelected(Boolean(editingWant.make || editingWant.model));
      setBrandOnly(Boolean(editingWant.make) && !editingWant.model);

      // Arrived from a listing this want doesn't match: auto-fill only the
      // offending fields to the listing's values and flag them for highlight.
      // Uses the same criteria as the matcher (getWantMismatchFields).
      if (matchListing) {
        const mismatches = getWantMismatchFields(editingWant, matchListing);
        const flags = {};
        if ('maxPrice' in mismatches) {
          setMaxPrice(String(mismatches.maxPrice));
          flags.maxPrice = { from: editingWant.maxPrice ? `$${editingWant.maxPrice}` : null };
        }
        if ('condition' in mismatches) {
          setCondition(mismatches.condition);
          flags.condition = { from: editingWant.condition || null };
        }
        if ('decade' in mismatches) {
          setDecade(mismatches.decade);
          flags.decade = { from: editingWant.decade || null };
        }
        if ('color' in mismatches) {
          setColor(mismatches.color);
          flags.color = { from: editingWant.color || null };
        }
        setHighlightFields(flags);
      } else {
        setHighlightFields({});
      }
    } else {
      setHighlightFields({});
      const urlParams = getUrlParams();
      if (urlParams.prefill) {
        // Pre-fill from URL parameters
        setMake(urlParams.make);
        setModel(urlParams.model);
        setCondition(urlParams.condition);
        
        // Use exact listing price 
        setMaxPrice(urlParams.price);
        setEffectTypes(urlParams.effectTypes || []);
        setDecade('');
        setDescription(''); // Don't pre-fill description, use default placeholder

        const prefillQuery = [urlParams.make, urlParams.model].filter(Boolean).join(' ').trim();
        setPedalQuery(prefillQuery);
        setPedalSelected(Boolean(urlParams.make || urlParams.model));
        setBrandOnly(Boolean(urlParams.make) && !urlParams.model);
      } else {
        // Clear form for new entry
        setMake('');
        setModel('');
        setMaxPrice('');
        setCondition('');
        setDecade('');
        setDescription('');
        setPedalQuery('');
        setPedalSelected(false);
        setBrandOnly(false);
      }
    }
  }, [editingWant, matchListing, location.search, getUrlParams]);

  // Fetch the user's wants once and compare in memory, rather than querying on
  // exact make+model equality. Firestore's `==` cannot see that "ZVEX" and
  // "Zvex" are one brand, and it forced a query per keystroke; a single read of
  // the user's own wants (a handful of small docs) covers every comparison the
  // identity rule needs, including brand-only wants that have no model to
  // compare on at all.
  // Depend on the identity of the edited want rather than the object: it is a
  // prop, and if the parent hands over a freshly built object each render,
  // depending on it would rebuild this callback every render and re-issue the
  // read in a loop.
  const editingWantId = editingWant?.id || null;
  const editingWantMake = editingWant?.make || '';
  const editingWantModel = editingWant?.model || '';

  const checkForDuplicate = useCallback(async () => {
    const user = currentUser;

    if (!user || (!make && !model)) return null;

    const wantsRef = collection(db, 'wantList');
    const snapshot = await getDocs(query(wantsRef, where('uid', '==', user.uid)));
    const wants = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Editing is no longer exempt from the check, but it is only blocked when
    // the edit is what creates the collision — see findBlockingDuplicate.
    return findBlockingDuplicate(
      wants,
      { make, model },
      {
        editingWant: editingWantId
          ? { id: editingWantId, make: editingWantMake, model: editingWantModel }
          : null,
      }
    );
  }, [currentUser, make, model, editingWantId, editingWantMake, editingWantModel]);

  // Advisory warning shown while typing. `currentUser` is in the dependency
  // list because it arrives asynchronously: without it, a form that was already
  // prefilled (the "add to Wantlist" link from a listing) ran this once before
  // sign-in resolved, bailed, and never re-ran — leaving the warning silently
  // off for the rest of the visit.
  useEffect(() => {
    let cancelled = false;
    // Debounced because `make` and `model` change on every keystroke in the
    // manual Brand/Model fields, and each run reads the user's whole Wantlist.
    // Undebounced, typing a brand out by hand costs one full read per character.
    // Correctness never depends on this settling: handleSubmit runs the same
    // check without a delay and is what actually blocks a duplicate.
    const timer = setTimeout(() => {
      checkForDuplicate()
        .then((duplicate) => {
          if (!cancelled) setDuplicateWant(duplicate || null);
        })
        .catch((error) => {
          console.error('Error checking for duplicate wants:', error);
          if (!cancelled) setDuplicateWant(null);
        });
    }, DUPLICATE_CHECK_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [checkForDuplicate]);

  // Capitalize the manual Brand/Model fallback fields on blur (mirrors the old
  // behavior; only relevant when the user is typing a pedal that isn't in the
  // Reverb catalog).
  const handleManualBlur = (e) => {
    const { name, value } = e.target;
    const capitalized = capitalizeWords(value);
    if (capitalized === value) return;
    if (name === 'make') setMake(capitalized);
    else if (name === 'model') setModel(capitalized);
  };

  // Selecting a canonical catalog row locks in the exact make + model.
  const selectCombinedSuggestion = (s) => {
    setMake(s.make || '');
    setModel(s.model || '');
    setPedalQuery(s.title || `${s.make || ''} ${s.model || ''}`.trim());
    setPedalSelected(true);
    setBrandOnly(false);
    setShowCombinedSuggestions(false);
  };

  // Brand-only want: "Any <Brand> pedal" → make set, model intentionally blank.
  const selectBrandOnly = (brand) => {
    setMake(brand);
    setModel('');
    setPedalQuery(brand);
    setPedalSelected(true);
    setBrandOnly(true);
    setShowCombinedSuggestions(false);
  };

  // "Not this? Search again" — drop the current pick and reopen the live search.
  const resetPedalSelection = () => {
    setPedalSelected(false);
    setBrandOnly(false);
    setMake('');
    setModel('');
    setShowCombinedSuggestions(true);
  };

  // On a genuine (settled) no-match, seed the manual Brand/Model from a best-
  // effort split of the query (last word = model) so the user rarely retypes.
  // Runs only once combinedNoMatch is true, so the manual fields never flash.
  useEffect(() => {
    if (!combinedNoMatch) return;
    if (make || model) return;
    const q = combinedQueryTrimmed.replace(/\s+/g, ' ');
    const lastSpace = q.lastIndexOf(' ');
    setMake(lastSpace > 0 ? q.slice(0, lastSpace) : q);
    setModel(lastSpace > 0 ? q.slice(lastSpace + 1) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedNoMatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🎯 WANT FORM SUBMIT STARTED');
        console.log('🔍 Form values at start:', { make, model, condition, maxPrice, decade, description, color });
    
    // Prevent double submission
    if (isSubmitting) {
      console.log('⏸️ Already submitting, blocking duplicate submission');
      return;
    }

    const user = currentUser;
    if (!user) {
      console.log('❌ No user found');
      return;
    }

    setIsSubmitting(true);

    try {
      // Re-check at submit time rather than trusting `isDuplicate`. That flag is
      // set by an async read, so submitting before it resolved used to sail
      // straight past the guard — which is how same-day duplicate pairs got
      // created. Awaiting here is what makes the guard authoritative.
      try {
        const duplicate = await checkForDuplicate();
        if (duplicate) {
          console.log('❌ Duplicate want detected, blocking submission');
          setDuplicateWant(duplicate);
          alert('This pedal is already in your Wantlist. Use the link below the price field to open it.');
          return;
        }
      } catch (error) {
        console.error('Duplicate re-check failed, allowing save:', error);
      }

      console.log('🔍 WANT FORM VERIFICATION CHECK:', {
        currentUserExists: !!user,
        emailVerified: user?.emailVerified,
        firestoreUserExists: !!firestoreUser,
        phoneVerified: firestoreUser?.phoneVerified,
        isVerified: firestoreUser?.isVerified,
        editingWant: !!editingWant,
      });

      // New wants need the profile loaded; edits do not — the user already has
      // wants on file and a slow profile fetch must not strand them on
      // "Saving..." forever.
      if (user && !firestoreUser && !editingWant) {
        console.log('⏳ Still loading user data, blocking submission');
        alert('⏳ Loading user data, please wait...');
        return;
      }

      if (
        firestoreUser &&
        (!user?.emailVerified || !firestoreUser?.phoneVerified || !firestoreUser?.isVerified)
      ) {
        console.log('🚫 VERIFICATION FAILED - Blocking want submission');
        alert('❌ You must complete both email and phone verification before adding wants. Please complete your verification in your profile.');
        return;
      }

      console.log('🔍 Field values:', { make, model, condition, maxPrice, decade, description, effectTypes });

      const hasMake = make && make.trim() !== '';
      const hasModel = model && model.trim() !== '';
      const hasEffectTypes = effectTypes && effectTypes.length > 0;

      if (!hasMake && !hasModel && !hasEffectTypes) {
        console.error('❌ Must specify at least one criterion');
        alert('❌ Please specify at least one of:\n• Brand\n• Model\n• Effect Type(s)\n• Accessories');
        return;
      }

      if (!hasMake && hasModel) {
        console.error('❌ Cannot specify model without brand');
        alert('❌ If specifying a model, you must also specify the brand');
        return;
      }

      const trimmedDescription = (description || '').trim();
      if (trimmedDescription) {
        const validation = validateMessage(trimmedDescription);
        if (!validation.isValid) {
          alert(validation.error);
          return;
        }
      }

      console.log('🔄 About to start want creation/update process...');

      if (editingWant) {
        console.log('📝 About to update existing want in Firestore...');

        const docRef = doc(db, 'wantList', editingWant.id);
        await updateDoc(docRef, {
          make: make || null,
          model: model || null,
          maxPrice: maxPrice ? parseFloat(maxPrice) : null,
          condition: condition || null,
          decade: decade || null,
          description,
          color: color || null,
          effectTypes,
          excludedKeywords: excludedKeywords.slice(0, MAX_EXCLUSION_KEYWORDS),
        });

        console.log('✅ Want updated with ID:', editingWant.id);

        if (!suppressSuccessAlert) alert('Want updated!');
        onFinishEdit();
      } else {
        console.log('📝 About to add want to Firestore...');

        const hasSpecificPedal = make && make.trim() !== '' && model && model.trim() !== '';
        const defaultNotifyOnMatch = hasSpecificPedal;

        const newWantRef = await addDoc(collection(db, 'wantList'), {
          uid: user.uid,
          ownerID: user.uid,
          make: make || null,
          model: model || null,
          maxPrice: maxPrice ? parseFloat(maxPrice) : null,
          condition: condition || null,
          decade: decade || null,
          description,
          color: color || null,
          effectTypes,
          excludedKeywords: excludedKeywords.slice(0, MAX_EXCLUSION_KEYWORDS),
          notifyOnMatch: defaultNotifyOnMatch,
          createdAt: serverTimestamp(),
        });

        console.log('✅ Want added with ID:', newWantRef.id);

        try {
          await trackWantAdded({
            id: newWantRef.id,
            make,
            model,
            maxPrice: parseFloat(maxPrice),
            effectTypes,
          });
        } catch (error) {
          console.error('❌ Analytics tracking failed:', error);
        }

        if (!pedalSelected && make && model) {
          try {
            await trackMissingPedal(make, model, currentUser.uid);
          } catch (error) {
            console.error('❌ Missing pedal tracking failed:', error);
          }
        }

        if (!suppressSuccessAlert) alert('Want added!');
        maybePromptForPush('add_want');
        if (onWantAdded) onWantAdded();
      }

      setMake('');
      setModel('');
      setMaxPrice('');
      setCondition('');
      setDecade('');
      setDescription('');
      setColor('');
      setEffectTypes([]);
      setExcludedKeywords([]);
      setPedalQuery('');
      setPedalSelected(false);
      setBrandOnly(false);
      setShowCombinedSuggestions(false);
      setConditionPopoverDismissed(false);
      setDuplicateWant(null);

      if (getUrlParams().prefill) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (err) {
      console.error('Error saving want:', err);
      alert('Failed to save want. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Appearance-only derivations (no functional impact) -----------------
  // "Match strength" is a purely decorative fullness meter: it reflects how
  // many of the optional signals the user has filled in, mirroring the
  // Add Pedal wizard's progress styling. It does NOT gate submission.
  const isPrefillAdd = Boolean(getUrlParams().prefill) && !editingWant;
  const showWantIntro = !editingWant && !isPrefillAdd;
  const wantSignalCount = [
    make,
    model,
    maxPrice,
    condition,
    decade,
    color,
    description,
    effectTypes.length > 0 ? 'fx' : ''
  ].filter((v) => v && String(v).trim()).length;
  const wantFilledSegments = Math.max(1, Math.min(4, Math.ceil(wantSignalCount / 2)));
  const wantStrengthLabel =
    wantSignalCount >= 6 ? 'detailed' : wantSignalCount >= 4 ? 'strong' : wantSignalCount >= 2 ? 'good' : 'basic';

  const WANT_CARD_STYLE = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxSizing: 'border-box'
  };
  const wantCardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' };
  const wantCardTitleStyle = { fontSize: '1.05rem', fontWeight: 700, color: '#1f2937', fontFamily: 'Nunito, sans-serif' };
  const wantOptionalStyle = { fontSize: '0.85rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif' };
  // Shared by Max Price and the Narrow-it-down dropdowns. boxSizing must be
  // border-box or width:100% plus horizontal padding overflows the card on iOS.
  const pillControlStyle = {
    borderRadius: '25px',
    fontSize: '17px',
    verticalAlign: 'middle',
    borderWidth: '1px',
    fontWeight: 'normal',
    width: '100%',
    boxSizing: 'border-box',
    // Match the "Search for your pedal" input's 0.55rem vertical padding —
    // at 0.33rem these pills sat visibly thinner than the search box directly
    // above them (spotted on the Pixel 4a, 2026-08-20).
    paddingTop: '0.55rem',
    paddingBottom: '0.55rem',
    paddingLeft: '0.9rem',
    paddingRight: '0.9rem',
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="add-want-root-form"
      style={{ width: '100%' }}
    >
      {wantTip && <OnboardingTip key={wantTip} tipId={wantTip} onGotIt={dismissWantTip} delay={wantTipDelay} />}
      {showWantIntro && (
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ color: '#6b7280', fontSize: '1rem', lineHeight: 1.5, margin: '0 0 1rem', fontFamily: 'Nunito, sans-serif' }}>
            Add as much or as little as you&rsquo;d like. The more details you add, the stronger your matches.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '9999px',
                  backgroundColor: i < wantFilledSegments ? '#4db6ac' : '#e5e7eb',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.9rem', fontFamily: 'Nunito, sans-serif' }}>
            Match strength: <span style={{ fontWeight: 600, color: '#4b5563' }}>{wantStrengthLabel}</span>
          </div>
        </div>
      )}

      {/* Only show header when editing or pre-filled (parent pages provide header for normal add) */}
      {(editingWant || getUrlParams().prefill) && (
      <h2 className='text-xl font-bold mb-2'>
        {editingWant 
          ? 'Edit Wanted Pedal' 
            : <>Save to Wantlist <span style={{ color: '#C9A227' }}>(Pre-filled)</span></>}
      </h2>
      )}
      
      {getUrlParams().prefill && !editingWant && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-3 mb-4'>
          <p className='text-green-700 text-sm' style={{ fontFamily: 'Nunito' }}>
            <span className='font-medium'>✨ Pre-filled from listing!</span> 
            <br />
            Feel free to adjust details like Max Price or Min. Condition before saving to your Wantlist, to improve your matching criteria.
          </p>
        </div>
      )}

      {/* Shown when editing a want that didn't match the listing the user came
          from: we pre-filled the offending fields and highlighted them in gold. */}
      {editingWant && Object.keys(highlightFields).length > 0 && (
        <div style={{ border: '1px solid #E6C964', backgroundColor: 'rgba(201, 162, 39, 0.10)', borderRadius: '8px', padding: '0.75rem 0.875rem', marginBottom: '1rem' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#7A5C00', fontFamily: 'Nunito, sans-serif', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700 }}>Highlighted fields were updated</span> so this want matches the listing. Review them before saving — anything not highlighted already matched.
          </p>
        </div>
      )}

      {/* Card: The basics */}
      <div className="add-want-card" style={WANT_CARD_STYLE}>
        <div style={wantCardHeaderStyle}>
          <span style={wantCardTitleStyle}>The basics</span>
          <span style={wantOptionalStyle}>optional</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' }}>

        {/* Combined "search for your pedal" — same hook + matcher + dropdown UI
            as the Add Pedal wizard's Step 1, with want-only extras (brand-only
            row + optional model in the manual fallback). */}
        <div style={{ position: 'relative' }}>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Search for your pedal:
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            </span>
            <input
              type="text"
              value={pedalQuery}
              onChange={(e) => {
                setPedalQuery(e.target.value);
                setPedalSelected(false);
                setBrandOnly(false);
                setShowCombinedSuggestions(true);
                setMake('');
                setModel('');
              }}
              onFocus={() => { if (!pedalSelected) setShowCombinedSuggestions(true); }}
              placeholder="e.g. Boss, or ThorpyFX Field Marshal"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="border border-gray-300 focus:border-blue-500 focus:outline-none"
              style={{ width: '100%', borderRadius: '25px', fontSize: '17px', padding: '0.55rem 0.9rem 0.55rem 2.6rem', borderWidth: '1px', boxSizing: 'border-box' }}
            />
          </div>

          {!pedalSelected && showCombinedSuggestions && pedalQuery.trim().length >= 2 && (combinedSuggestions.length > 0 || combinedLoading) && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {combinedSuggestions.length === 0 && combinedLoading ? (
                <div style={{ color: '#6B7280', fontSize: '0.9rem', padding: '0.35rem 0' }}>Searching…</div>
              ) : (
                <>
                  {/* Brand-only option: "Any <Brand> pedal" (make set, model blank) */}
                  {brandOnlyCandidate && (
                    <button
                      type="button"
                      onClick={() => selectBrandOnly(brandOnlyCandidate)}
                      className="hover:bg-gray-50"
                      style={{ display: 'block', textAlign: 'left', background: 'white', border: '1px dashed #C9A227', borderRadius: '12px', padding: '0.7rem 0.95rem', cursor: 'pointer', width: '100%' }}
                    >
                      <span style={{ display: 'block', fontWeight: 700, color: '#1f2937', fontSize: '0.95rem' }}>Any {brandOnlyCandidate} pedal</span>
                      <span style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem' }}>Match any model from this brand</span>
                    </button>
                  )}
                  {combinedSuggestions.slice(0, 8).map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectCombinedSuggestion(s)}
                      className="hover:bg-gray-50"
                      style={{ display: 'block', textAlign: 'left', background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '0.7rem 0.95rem', cursor: 'pointer', width: '100%' }}
                    >
                      {s.make && (
                        <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{s.make}</span>
                      )}
                      <span style={{ display: 'block', fontWeight: 700, color: '#6b7280', fontSize: '1rem', lineHeight: 1.25 }}>{s.model || s.title}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Selected pedal chip (brand-only shows "Any model") */}
          {pedalSelected && (make || model) && (
            <div style={{ marginTop: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.9rem', background: '#e7f6ec', border: '1px solid #b7e4c7', borderRadius: '14px', padding: '0.9rem 1.1rem' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                {make && <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{make}</span>}
                <span style={{ display: 'block', fontWeight: 700, color: '#4b5563', fontSize: '1.05rem', lineHeight: 1.25 }}>{brandOnly ? 'Any model' : (model || pedalQuery)}</span>
              </span>
              <span style={{ color: '#2f9e57', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              </span>
            </div>
          )}

          {/* No-match → manual entry. For wants the model is OPTIONAL: leave it
              blank for a brand-only want. */}
          {combinedNoMatch && (
            <div style={{ marginTop: '0.9rem' }}>
              <div style={{ textAlign: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '1.1rem 1rem' }}>
                <div style={{ color: '#9ca3af', marginBottom: '0.4rem', display: 'flex', justifyContent: 'center' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                </div>
                <div style={{ fontWeight: 700, color: '#374151' }}>No matches for &ldquo;{pedalQuery.trim()}&rdquo;</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.15rem' }}>Check the spelling, or enter the brand &amp; model below — leave the model blank for a brand-only want.</div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: 600, textAlign: 'left' }}>
                  Brand:
                </label>
                <input
                  name="make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  onBlur={handleManualBlur}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  placeholder="e.g. Bonecrusher Fuzzworks"
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                  style={{ width: '100%', borderRadius: '25px', fontSize: '17px', paddingTop: '0.33rem', paddingBottom: '0.33rem', borderWidth: '1px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginTop: '0.8rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: 600, textAlign: 'left' }}>
                  Model <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>:
                </label>
                <input
                  name="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  onBlur={handleManualBlur}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  placeholder="Leave blank to want any model"
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                  style={{ width: '100%', borderRadius: '25px', fontSize: '17px', paddingTop: '0.33rem', paddingBottom: '0.33rem', borderWidth: '1px', boxSizing: 'border-box' }}
                />
              </div>

              {/* "Found it after all" — the manual text matched a real catalog
                  pedal (typo fixed / word finished). Tappable, never blocking. */}
              {manualMatches.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2f9e57', marginBottom: '0.4rem' }}>
                    Good news — this pedal is in our catalog. Tap to use the standard name:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {manualMatches.slice(0, 3).map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCombinedSuggestion(s)}
                        className="hover:bg-gray-50"
                        style={{ display: 'block', textAlign: 'left', background: 'white', border: '1px solid #b7e4c7', borderRadius: '12px', padding: '0.7rem 0.95rem', cursor: 'pointer', width: '100%' }}
                      >
                        {s.make && (
                          <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{s.make}</span>
                        )}
                        <span style={{ display: 'block', fontWeight: 700, color: '#6b7280', fontSize: '1rem', lineHeight: 1.25 }}>{s.model || s.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {(pedalSelected || combinedNoMatch) && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button type="button" onClick={resetPedalSelection} style={{ background: 'none', border: 'none', color: '#5A9ECB', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
                Not this? Search again
              </button>
            </div>
          )}

          {/* Type-only hint: a want needs no specific pedal at all. */}
          {!pedalSelected && !combinedNoMatch && (
            <div style={{ marginTop: '0.5rem', color: '#9ca3af', fontSize: '0.8rem', textAlign: 'left' }}>
              Looking for something broad? Leave this blank and pick an effect type below.
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Max Price:
          </label>
          <input 
            type='number' 
            value={maxPrice} 
            onChange={(e) => { setMaxPrice(formatCurrencyInput(e.target.value)); clearHighlight('maxPrice'); }} 
            onWheel={(e) => e.target.blur()}
            className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3" 
            style={{ ...pillControlStyle, ...(highlightFields.maxPrice ? highlightInputStyle : {}) }}
          />
          <HighlightCaption field="maxPrice" />
        </div>

        {/* Duplicate warning. Shown whenever the check finds a collision, even
            when the button stays enabled — see duplicateBlocksSave.
            The link matters as much as the warning: telling someone a pedal is
            already on their list without showing them where it is leaves them to
            go hunting for it, which is the whole reason the listing page's
            version of this has always offered to open the existing want. */}
        {duplicateWant && !editingIdentityUnchanged && (
          <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <p style={{ 
              fontFamily: 'Nunito, sans-serif', 
              color: '#AC4320', 
              fontSize: '16px',
              margin: 0,
              lineHeight: '1.4'
            }}>
              {editingWant
                ? 'Another want on your list is already for this pedal.'
                : 'This pedal is already in your Wantlist.'}
            </p>
            <button
              type='button'
              onClick={() => {
                scrollPageToTop();
                navigate(`/addpedal?tab=want&edit=${encodeURIComponent(duplicateWant.id)}`, {
                  state: { tab: 'want' }
                });
              }}
              style={{
                marginTop: '0.35rem',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                color: '#5A9ECB',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              {`Open my ${[duplicateWant.make, duplicateWant.model].filter(Boolean).join(' ') || 'existing'} want`}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Card: Narrow it down */}
      <div className="add-want-card" style={WANT_CARD_STYLE}>
        <div style={wantCardHeaderStyle}>
          <span style={wantCardTitleStyle}>Narrow it down</span>
          <span style={wantOptionalStyle}>optional</span>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Min Condition:
          </label>
          <select 
            ref={conditionSelectRef}
            value={condition} 
            onChange={(e) => {
              setCondition(e.target.value);
              clearHighlight('condition');
              setConditionPopoverDismissed(false);
            }} 
            className="pxc-form-select"
            style={{ ...FORM_SELECT_STYLE, ...(highlightFields.condition ? highlightInputStyle : {}) }}
          >
            <option value=''>Any</option>
            <option value='New'>New</option>
            <option value='Excellent'>Excellent</option>
            <option value='Very Good'>Very Good</option>
            <option value='Good'>Good</option>
            <option value='Poor'>Poor</option>
            <option value='Damaged'>Damaged</option>
          </select>
          <HighlightCaption field="condition" />
        </div>

        <ConditionDescriptionPopover
          anchorRef={conditionSelectRef}
          visible={Boolean(condition) && !conditionPopoverDismissed}
          description={CONDITION_DESCRIPTIONS[condition]}
          onDismiss={() => setConditionPopoverDismissed(true)}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem', rowGap: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Decade:
          </label>
          <select 
            value={decade} 
            onChange={(e) => { setDecade(e.target.value); clearHighlight('decade'); }} 
            className="pxc-form-select"
            style={{ ...FORM_SELECT_STYLE, ...(highlightFields.decade ? highlightInputStyle : {}) }}
          >
            <option value=''>Any</option>
            <option value='2020s'>2020s</option>
            <option value='2010s'>2010s</option>
            <option value='2000s'>2000s</option>
            <option value='1990s'>1990s</option>
            <option value='1980s'>1980s</option>
            <option value='1970s'>1970s</option>
            <option value='1960s'>1960s</option>
            <option value='1950s'>1950s</option>
            <option value='Older'>Older</option>
          </select>
          <HighlightCaption field="decade" />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Color:
          </label>
          <select 
            value={color} 
            onChange={(e) => { setColor(e.target.value); clearHighlight('color'); }} 
            className="pxc-form-select"
            style={{ ...FORM_SELECT_STYLE, ...(highlightFields.color ? highlightInputStyle : {}) }}
          >
            <option value=''>Any</option>
            {PEDAL_COLORS.map(colorOption => (
              <option key={colorOption} value={colorOption}>{colorOption}</option>
            ))}
          </select>
          <HighlightCaption field="color" />
        </div>
        </div>
      </div>

      {/* Card: Effect type */}
      <div className="add-want-card" style={WANT_CARD_STYLE}>
        <div style={wantCardHeaderStyle}>
          <span style={wantCardTitleStyle}>Effect type</span>
          <span style={wantOptionalStyle}>optional</span>
        </div>
        <style jsx>{`
          .want-effect-type-btn:hover {
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
            transform: translateY(-1px);
          }
          @media (max-width: 768px) {
            .want-effect-types-grid {
              gap: 0.3rem !important;
            }
            .want-effect-type-btn {
              padding: 6px 14px !important;
              font-size: 0.8rem !important;
            }
            .want-effect-type-label {
              margin-top: 0.75rem !important;
            }
          }
        `}</style>
        <div 
          className="want-effect-types-grid"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.25rem' }}
        >
          {EFFECT_TYPES.map((type) => {
            const isSelected = effectTypes.includes(type);
            return (
              <button
                key={type}
                type="button"
                className="want-effect-type-btn"
                onClick={() => {
                  setEffectTypes((prev) => {
                    const updated = isSelected
                      ? prev.filter((t) => t !== type)
                      : [...prev, type];
                    return updated;
                  });
                }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '9999px',
                  border: isSelected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
                  backgroundColor: isSelected ? 'rgba(201, 162, 39, 0.12)' : 'white',
                  color: isSelected ? '#946F00' : '#374151',
                  fontWeight: isSelected ? '700' : '500',
                  fontSize: '0.9rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {type}
              </button>
            );
          })}
        </div>

        {/* Accessories */}
        <label className="font-bold" style={{ display: 'block', marginBottom: '0.25rem', marginTop: '1.5rem' }}>
          Accessories:
        </label>
        <div style={{ height: '0.75rem' }} />
        <div 
          className="want-effect-types-grid"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}
        >
          {[
            'Power Supply', 'Pedalboard', 'Other Accessory'
          ].map((type) => {
            const isSelected = effectTypes.includes(type);
            return (
              <button
                key={type}
                type="button"
                className="want-effect-type-btn"
                onClick={() => {
                  setEffectTypes((prev) => {
                    const updated = isSelected
                      ? prev.filter((t) => t !== type)
                      : [...prev, type];
                    return updated;
                  });
                }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '9999px',
                  border: isSelected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
                  backgroundColor: isSelected ? 'rgba(201, 162, 39, 0.12)' : 'white',
                  color: isSelected ? '#946F00' : '#374151',
                  fontWeight: isSelected ? '700' : '500',
                  fontSize: '0.9rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {type}
              </button>
            );
          })}
        </div>
      </div>

      {/* hideNotes is the onboarding flow's "keep this step short" switch. A user
          adding their very first want has no matches yet and so nothing to
          exclude, which makes signup the wrong moment to introduce the idea. The
          card is available everywhere else, including when they later edit this
          same want. */}
      {!hideNotes && (
        <div className="add-want-card" style={WANT_CARD_STYLE}>
          <div style={wantCardHeaderStyle} data-tip-anchor="never-show-me">
            <span style={wantCardTitleStyle}>Never show me</span>
            <span style={wantOptionalStyle}>optional</span>
          </div>
          <KeywordExclusionInput
            value={excludedKeywords}
            onChange={setExcludedKeywords}
            wantCriteria={{ make, model, effectTypes }}
          />
        </div>
      )}

      {!hideNotes && (
        <div className="add-want-card" style={WANT_CARD_STYLE}>
          <div style={wantCardHeaderStyle}>
            <span style={wantCardTitleStyle}>Anything else</span>
            <span style={wantOptionalStyle}>optional</span>
          </div>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="add any notes or details you want to track" 
            className="w-full border border-gray-300 focus:border-blue-500 focus:outline-none px-3 py-2 want-description-textarea" 
            style={{ borderRadius: '12px', display: 'block', width: '100%', boxSizing: 'border-box' }}
            rows={5} 
          />
        </div>
      )}

      <button
        type='submit'
        // `isDuplicate` no longer needs an editing exemption: the check excludes
        // the want being edited, so a hit here is a collision with a different
        // want and should block the save either way. It does need the
        // duplicateBlocksSave qualifier, so a half-typed pedal name can't grey
        // the button out mid-form.
        disabled={isSubmitting || duplicateBlocksSave}
        className='hover:opacity-80 transition-opacity'
        style={{
          backgroundColor: (isSubmitting || duplicateBlocksSave) ? '#9CA3AF' : '#C9A227',
          borderRadius: '9999px',
          border: 'none',
          display: 'block',
          width: '100%',
          padding: '12px 24px',
          fontFamily: 'Nunito, sans-serif',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
          marginTop: '0.5rem',
          cursor: (isSubmitting || duplicateBlocksSave) ? 'not-allowed' : 'pointer',
          opacity: (isSubmitting || duplicateBlocksSave) ? 0.6 : 1
        }}
      >
        {isSubmitting ? 'Saving...' : (editingWant ? 'Update Want' : 'Add Want')}
      </button>

      {editingWant && (
        <button 
          type='button' 
          onClick={onFinishEdit} 
          className='hover:opacity-80 transition-opacity'
          style={{
            backgroundColor: '#E5E7EB',
            borderRadius: '9999px',
            border: 'none',
            padding: '6px 14px',
            fontFamily: 'Nunito, sans-serif',
            color: '#374151',
            fontWeight: 'bold',
            fontSize: '15px',
            marginTop: '12px',
            marginLeft: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      )}

      {/* Prefill (add-from-listing) Cancel — lets the user dismiss the deep-link
          form and drop back to their Wantlist without saving. Only shown for the
          prefill add flow (edit mode has its own Cancel above). */}
      {!editingWant && getUrlParams().prefill && onCancelPrefill && (
        <button 
          type='button' 
          onClick={onCancelPrefill} 
          className='hover:opacity-80 transition-opacity'
          style={{
            backgroundColor: '#E5E7EB',
            borderRadius: '9999px',
            border: 'none',
            padding: '6px 14px',
            fontFamily: 'Nunito, sans-serif',
            color: '#374151',
            fontWeight: 'bold',
            fontSize: '15px',
            marginTop: '12px',
            marginLeft: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      )}

      {editingWant && (
        <>
          <style>{`
            .floating-save-changes-btn {
              position: fixed;
              bottom: 42px;
              left: 30px;
              z-index: 1000;
              border-radius: 9999px;
              border: none;
              padding: 14px 30px;
              font-family: Nunito, sans-serif;
              color: white;
              font-weight: 700;
              font-size: 17px;
              background-color: #C9A227;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              transition: background-color 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
            }
            .floating-save-changes-btn:disabled {
              cursor: not-allowed;
              opacity: 0.7;
            }
            /* Hover in CSS behind a hover query rather than JS mouse handlers:
               iOS synthesizes mouseenter on whatever a tap lands near and only
               clears it on the next tap elsewhere, which made this always-visible
               fixed button pulse on every tap. Mirrors AddPedalForm. */
            @media (hover: hover) and (pointer: fine) {
              .floating-save-changes-btn:not(:disabled):hover {
                background-color: #B89020;
                transform: scale(1.04);
              }
            }
            @media (max-width: 768px) {
              .floating-save-changes-btn {
                bottom: calc(92px + env(safe-area-inset-bottom));
                left: 14px;
                padding: 12px 24px;
                font-size: 16px;
              }
            }
          `}</style>
          <button
            type="submit"
            disabled={isSubmitting || duplicateBlocksSave}
            className="floating-save-changes-btn"
            aria-label="Save changes to this want"
          >
            {isSubmitting ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    </form>
  );
};

export default AddWantForm;
